// ---------- dictionnaire portugais (Brésil) ----------
// Copie conforme de fr.ts, clé pour clé — typé `Record<I18nKey, string>` ci-dessous, donc une clé
// manquante ou en trop est une ERREUR DE COMPILATION, jamais un texte français qui atteindrait la
// production. Voir fr.ts pour les conventions suivies ici (clés plates, `{{var}}` portant la
// phrase entière, les trois registres de shared/ traités à part dans ./index.ts).
//
// Portugais du BRÉSIL et non du Portugal : c'est le marché visé, et les deux normes diffèrent
// jusque dans le vocabulaire de l'interface (« tela » et non « ecrã », « apagar » et non
// « eliminar »). Adresse en « você », l'équivalent du tutoiement du français ici.
import type { I18nKey } from './fr';

const pt: Record<I18nKey, string> = {
  // ---------- marque (index.html, p.html, splash.ts) ----------
  'brand.name': 'Codigrid',

  // ---------- index.html <head> ----------
  'app.title': 'Codigrid — grade de padrões',
  'app.description':
    'Um jogo de colorir lógico: uma imagem vira uma grade de símbolos codificados.',

  // ---------- commun (plusieurs dialogues) ----------
  'common.close': 'Fechar',
  'common.cancel': 'Cancelar',
  'common.back': 'Voltar',

  // ---------- home.ts / index.html #home ----------
  'home.filter.doing': 'Em andamento',
  'home.filter.done': 'Concluídos',
  'home.tab.feed': 'Meu feed',
  'home.tab.daily': 'Diário',
  'home.tab.library': 'Biblioteca',
  'home.tab.settings': 'Ajustes',
  'home.tab.create': 'Criar',
  'home.tab.new': 'Novo',
  'home.category.celebrite': 'Celebridades',
  'home.category.personnes': 'Retratos',
  'home.category.monuments': 'Monumentos',
  'home.category.scenes': 'Cenas do cotidiano',
  'home.category.contes': 'Contos e lendas',
  'home.category.saisonnier': 'Sazonal',
  'home.category.paysage': 'Paisagens',
  'home.category.animaux': 'Animais',
  'home.category.vehicules': 'Veículos',
  'home.category.sport': 'Esporte',
  'home.category.art': 'Arte',
  'home.category.divers': 'Diversos',
  'home.category.other': 'Outros',
  'home.facet.category.title': 'Categoria',
  'home.facet.category.all': 'Todas',
  'home.facet.difficulty.title': 'Dificuldade',
  'home.facet.difficulty.all': 'Todas',
  'home.facet.alphabet.title': 'Alfabeto',
  'home.facet.alphabet.all': 'Todos',
  // « células » reste invariable ici, comme en français : la plus petite grille fait 4 × 4, donc
  // le singulier ne s'affiche jamais.
  'home.card.tooltip': '{{w}} × {{h}} células · {{diff}}',
  'home.card.new': 'Novo',
  // ---------- a duração indicada num cartão (home.ts, shared/effort.ts) ----------
  'home.card.eta': '≈ {{d}}',
  'home.card.etaLong': 'cerca de {{d}} no total',
  'home.card.leftLong': 'faltam cerca de {{d}}',
  'time.unit.min': 'min',
  'time.unit.h': 'h',
  'time.min': '{{n}} min',
  'time.h': '{{n}} h',
  'time.hmin': '{{h}} h {{m}}',
  'time.hplus': '{{n}} h+',
  'home.empty.noMatch': 'Nenhum quebra-cabeça corresponde a esses filtros ainda.',
  'home.empty.resetFilters': 'Limpar os filtros',
  'home.empty.libraryDone': 'Você já começou todos os disponíveis. Volte depois: vem mais aí.',
  'home.empty.libraryDonePacks':
    'Você já começou todos os seus quebra-cabeças. Novos pacotes esperam por você.',
  'home.empty.seePacks': 'Ver os pacotes',
  'home.empty.puzzleGone': 'Este quebra-cabeça não está disponível.',
  'home.empty.retry': 'Tentar de novo',
  'home.empty.noneDoing': 'Nenhum em andamento. Escolha um na biblioteca.',
  'home.empty.openLibrary': 'Abrir a biblioteca',
  'home.empty.noneDone': 'Nada concluído ainda. Logo vem.',
  'home.empty.noPuzzles': 'Nenhum quebra-cabeça disponível ainda.',
  'home.empty.serverError': 'Não foi possível falar com o servidor.',
  'home.loading': 'Carregando…',
  // ---------- o cartão de avaliação do fluxo (home.ts::rateCard, regras em review.ts) ----------
  'home.rate.1.title': 'Agora você conhece bem o Codigrid',
  'home.rate.1.body':
    'Sua avaliação na Play Store ajuda outros fãs de colorir a saber se o jogo é para eles.',
  'home.rate.1.cta': 'Compartilhar minha opinião',
  'home.rate.2.title': 'O Codigrid está te divertindo?',
  'home.rate.2.body':
    'Conte em 10 segundos na Play Store: para um jogo pequeno, isso faz toda a diferença.',
  'home.rate.2.cta': 'Avaliar o Codigrid',
  'home.rate.3.title': 'Quer novos quebra-cabeças?',
  'home.rate.3.body':
    'Cada avaliação na Play Store conta: é o que me permite continuar criando novos.',
  'home.rate.3.cta': 'Deixar minha avaliação',
  'home.progression.open': 'Progresso',
  'home.packsBanner.sub': '{{n}} pacotes para desbloquear',
  'home.packsBanner.one': '1 pacote para desbloquear',
  'home.packsBanner.cta': 'Descobrir',

  // ---------- criações: home.ts (cartão, filtro, cartões) e creer.ts (a ferramenta) ----------
  'home.create.title': 'Criar meu quebra-cabeça',
  'home.create.sub': 'A partir de uma das suas fotos',
  'home.facet.mine': 'Minhas criações',
  'home.pack.base': 'Para começar',
  'home.card.mine': 'Minha criação',
  'home.empty.noCreations':
    'Você ainda não criou nenhum quebra-cabeça. Suas criações vão aparecer aqui.',
  'home.creation.delete': 'Excluir esta criação',
  'home.creation.deleteConfirm':
    'Excluir “{{title}}”? O quebra-cabeça e seu progresso nele serão perdidos.',
  'creer.step': '{{n}} / {{total}}',
  'creer.next': 'Continuar',
  'creer.busy': 'Preparando a folha…',
  'creer.photo.title': 'Sua foto',
  'creer.photo.camera': 'Tirar uma foto',
  'creer.photo.gallery': 'Minha galeria',
  'creer.photo.tipsTitle': 'As fotos que dão os quebra-cabeças mais bonitos',
  'creer.photo.tip1': 'Um tema que preencha o quadro',
  'creer.photo.tip1Sub': 'Um rosto, um animal, uma silhueta, vistos de perto.',
  'creer.photo.tip2': 'Contraste',
  'creer.photo.tip2Sub': 'Claros e escuros bem marcados: são eles que desenham.',
  'creer.photo.tip3': 'Uma foto nítida, com boa luz',
  'creer.photo.tip3Sub': 'Luz do dia, sem contraluz nem desfoque.',
  'creer.photo.tip4': 'Nada de detalhes minúsculos',
  'creer.photo.tip4Sub': 'Um texto, uma multidão ou uma paisagem distante se perdem nas células.',
  'creer.photo.cutTitle': 'Um retrato? Tire o fundo antes',
  'creer.photo.cutText':
    'Com uma ferramenta como {{link}}, por exemplo; depois escolha a imagem em Minha galeria: o fundo vira papel.',
  'creer.photo.private':
    '<b>Sua foto nunca é enviada.</b> A grade é feita no seu celular, e só você vê o quebra-cabeça.',
  'creer.photo.error': 'Não foi possível ler esta imagem. Tente outra foto.',
  'creer.crop.title': 'Enquadrar',
  'creer.crop.hint': 'Pince para dar zoom, arraste para mover',
  'creer.crop.photo': 'Foto',
  'creer.crop.square': 'Quadrado',
  'creer.crop.portrait': 'Retrato',
  'creer.crop.landscape': 'Paisagem',
  'creer.crop.rotate': 'Girar um quarto de volta',
  'creer.adjust.title': 'Sua folha',
  'creer.tool.tech': 'Técnica',
  'creer.tool.detail': 'Detalhe',
  'creer.tool.light': 'Luz',
  'creer.light.normal': 'Normal',
  'creer.light.clear': 'Claro',
  'creer.light.notch': 'Luz {{n}} de {{total}}',
  'creer.tool.contrast': 'Contraste',
  'creer.contrast.soft': 'Natural',
  'creer.contrast.strong': 'Marcado',
  'creer.contrast.notch': 'Contraste {{n}} de {{total}}',
  'creer.zoomReset': 'Ver a prévia inteira',
  'creer.detail.cells': 'Número de células',
  'creer.detail.readout': '{{w}} × {{h}} · {{n}} células',
  'creer.detail.notch': '{{n}} células de lado',
  'creer.detail.less': 'Rápido de pintar',
  'creer.detail.more': 'Muitos detalhes',
  'creer.ready.title': 'Resumo',
  'creer.ready.name': 'O nome dele',
  'creer.ready.placeholder': 'Meu quebra-cabeça',
  'creer.ready.photo':
    '<b>Sua foto fica no seu celular.</b> Ela nunca é enviada, e o quebra-cabeça feito com ela também não.',
  'creer.ready.private':
    '<b>Este quebra-cabeça é só seu, neste celular.</b> Ele não fica guardado em nenhum outro lugar: se você desinstalar o jogo ou trocar de celular, ele não vai junto.',
  'creer.ready.go': 'Salvar o quebra-cabeça',
  'creer.ready.saving': 'Salvando…',
  'creer.ready.storeFailed':
    'Não foi possível salvar o quebra-cabeça neste celular. Libere um pouco de espaço e tente de novo.',
  'creer.ready.tooMany':
    'Você já tem {{n}} criações, o máximo. Exclua uma em “Minhas criações” para fazer outra.',
  'creer.ready.full': 'A criação de quebra-cabeças está indisponível no momento. Tente mais tarde.',
  'creer.ready.freeLeftMany': 'Mais {{n}} criações grátis, sem anúncios.',
  'creer.ready.freeLeftOne': 'Mais 1 criação grátis, sem anúncios.',
  'creer.ready.adNote':
    'Sua primeira criação foi grátis. Esta é salva depois de um anúncio curto: é o que mantém o jogo vivo.',
  'creer.ready.goAd': 'Ver um anúncio e salvar',
  'creer.ready.adSkipped':
    'Anúncio interrompido: sua criação ainda não foi salva. Tente de novo quando quiser.',
  'creer.ready.adUnavailable': 'Nenhum anúncio disponível agora. Tente de novo daqui a pouco.',

  // ---------- daily.ts / index.html #dailyPage (o puzzle do dia) ----------
  'daily.month.prev': 'Mês anterior',
  'daily.month.next': 'Mês seguinte',
  'daily.state.todo': 'Para pintar',
  'daily.state.doing': 'Em andamento, {{pct}} %',
  'daily.state.done': 'Concluído',
  'daily.cell.aria': '{{date}} — {{state}}',
  'daily.empty.stock': 'Hoje não tem puzzle. Volte amanhã.',
  'daily.empty.update': 'Este puzzle precisa de uma versão mais recente do aplicativo.',
  'daily.today.kicker': 'A folha de hoje',
  'daily.today.unknown': 'Ninguém sabe ainda o que é',
  'daily.today.paint': 'Pintar',
  'daily.today.resume': 'Continuar',
  'daily.today.see': 'Ver de novo',

  // ---------- packs.ts / index.html #packs (pacotes desbloqueáveis) ----------
  'packs.title': 'Pacotes disponíveis',
  'packs.carousel': 'carrossel',
  'packs.loading': 'Carregando…',
  'packs.empty': 'Nada para desbloquear ainda. Volte depois: vem mais aí.',
  'packs.puzzlesLabel.one': 'quebra-cabeça',
  'packs.puzzlesLabel.other': 'quebra-cabeças',
  'packs.selection': 'Seleção',
  'packs.unlock': 'Desbloquear',
  'packs.watchAd': 'Assistir a um anúncio',
  // ---------- les arguments d'un pack (src/packArgument.ts) ----------
  'packs.arg.debutant': 'Ótimo para começar',
  'packs.arg.portraits': 'Rostos',
  'packs.arg.patience': 'Sem pressa',
  'packs.arg.nouveau': 'Recém-chegado',
  'packs.arg.costaud': 'Grades grandes',
  'packs.arg.express': 'Partidas curtas',
  'packs.arg.mono': 'Uma única técnica',
  'packs.arg.varie': 'Técnicas variadas',
  'packs.arg.couleur': 'Colorido',
  'packs.unlocking': 'Desbloqueando…',
  // Sous le bouton Desbloquear : dit qu'il n'y a pas d'argent en jeu — cf. src/packs.ts,
  // unlockNote(). Le `<b>` est du balisage voulu, même précédent que home.intro.
  'packs.adNote': '<b>Grátis</b> · cerca de 30 s',
  'packs.freeNote': '<b>Grátis</b>, com um toque',
  'packs.adLoading': 'Carregando o anúncio…',
  'packs.adUnavailable': 'Nenhum anúncio disponível — tente de novo.',
  'packs.adSkipped': 'O anúncio não terminou.',
  'packs.adFailed': 'Não deu certo — tente de novo.',
  'packs.reveal.title.one': '{{n}} quebra-cabeça desbloqueado!',
  'packs.reveal.title.other': '{{n}} quebra-cabeças desbloqueados!',
  'packs.reveal.sub': '“{{title}}” entrou na sua Biblioteca',
  'packs.reveal.continue': 'Continuar',
  'packs.feature.coeur': 'Nosso favorito',
  'packs.feature.saison': 'Da estação',
  'packs.feature.nouveau': 'Novo',
  'packs.feature.evenement': 'Evento especial',
  'packs.feature.joueurs': 'Favorito dos jogadores',
  'packs.feature.populaire': 'O mais desbloqueado',
  'packs.volume': '{{title}} nº {{n}}',
  'packs.intro': '{{n}} quebra-cabeças para descobrir',
  'packs.shelf.n': '{{n}} pacotes',
  'packs.unlocks': 'Desbloqueado por {{n}} jogadores',
  'packs.fact.time': 'para colorir',
  'packs.fact.votes': '{{n}} avaliações',
  'packs.fact.unrated': 'ainda sem avaliação',
  'packs.inside': 'O que tem dentro',
  'packs.inside.hint': 'Cada tema aparece enquanto você colore.',
  'packs.sheet.puzzle': 'Quebra-cabeça {{n}}',
  'technique.family.motifs': 'Padrões',
  'technique.family.hachures': 'Hachuras',
  'technique.family.ascii': 'ASCII',
  'technique.family.pointille': 'Pontilhismo',
  'technique.family.des': 'Dados',
  'technique.family.briques': 'Tijolos',
  'technique.family.peinture': 'Pintura',
  'technique.family.croix': 'Bordado',

  // ---------- progression.ts / index.html #progression (medalhas + Juntos) ----------
  'progression.title': 'Progresso',
  'progression.tier.bronze': 'Bronze',
  'progression.tier.argent': 'Prata',
  'progression.tier.or': 'Ouro',
  'progression.tier.platine': 'Platina',
  'progression.loading': 'Carregando…',
  'progression.needAccount':
    'Conclua um quebra-cabeça para ver o que os outros jogadores estão pintando.',
  'progression.loadFailed': 'Não foi possível falar com o servidor.',
  // L’écran « Moi » et ses badges (src/profile.ts) — les règles, seuils compris, viennent du serveur.
  'progression.tab.me': 'Eu',
  // L’onglet « Ensemble » (src/together.ts) et la rareté des badges — ce qui a remplacé la ligue.
  'progression.tab.together': 'Juntos',
  'me.rarity': '{{pct}}% dos jogadores',
  'me.rarity.rare': 'Raro · {{pct}}% dos jogadores',
  'me.rarity.locked': 'Já conquistado por {{pct}}% dos jogadores',
  'together.eyebrow': 'Esta semana · desde segunda',
  'together.sheets.one': 'quebra-cabeça concluído',
  'together.sheets.other': 'quebra-cabeças concluídos',
  'together.players.one': 'por {{n}} jogador',
  'together.players.other': 'por {{n}} jogadores',
  'together.stats': 'A semana em números',
  'together.trend.eyebrow': 'Tendência',
  'together.trend.up': 'em relação à semana passada',
  'together.trend.same': 'como na semana passada',
  'together.trend.calm': 'um pouco mais calma que a semana passada',
  'together.trend.ctx': '{{n}} quebra-cabeças, contra {{last}} no mesmo horário da semana passada.',
  'together.peak.eyebrow': 'Horário de pico',
  'together.peak.what': 'o horário em que mais se colore',
  'together.crossed.eyebrow': 'Medalhas',
  'together.crossed.one': 'nível conquistado desde segunda',
  'together.crossed.other': 'níveis conquistados desde segunda',
  'together.joined.eyebrow': 'Boas-vindas',
  'together.joined.one': 'novo jogador esta semana',
  'together.joined.other': 'novos jogadores esta semana',
  'together.joined.ctx': 'Na semana passada, no mesmo horário: {{last}}.',
  'together.empty':
    'Ninguém concluiu um quebra-cabeça ainda esta semana. O primeiro pode ser o seu.',
  'together.mine.title': 'Sua parte da semana.',
  'together.mine.zero': 'Nenhum quebra-cabeça ainda esta semana. Sem pressa.',
  'together.mine.one': '1 quebra-cabeça concluído de {{total}}.',
  'together.mine.other': '{{n}} quebra-cabeças concluídos de {{total}}.',
  'together.daily.one': '{{n}} jogador já pintou o quebra-cabeça do dia.',
  'together.daily.other': '{{n}} jogadores já pintaram o quebra-cabeça do dia.',
  'together.daily.with': 'Com você, {{n}} jogadores pintaram o quebra-cabeça do dia.',
  'together.daily.go': 'Pintar',
  'progression.tier.prisme': 'Prisma',
  'me.eyebrow': 'Seu apelido',
  'me.edit': 'Mudar de apelido',
  'me.choose': 'Escolha seu apelido',
  'me.since': 'Jogando desde {{date}}',
  'me.days.one': '{{n}} dia',
  'me.days.other': '{{n}} dias',
  'me.numbers': 'Seus números',
  'me.cells.eyebrow': 'Células pintadas',
  'me.cells.what': 'desde que você chegou',
  'me.fav.eyebrow': 'Seu estilo',
  'me.fav.cat': 'Categoria favorita: {{cat}}',
  'me.biggest.eyebrow': 'Seu maior quebra-cabeça',
  'me.biggest.cells': '{{n}} células pintadas',
  'me.biggest.untitled': 'Sem título',
  'me.activity.title': 'Suas últimas 12 semanas',
  'me.activity.more': 'mais',
  'me.activity.less': 'menos',
  'me.activity.today': 'hoje',
  'me.activity.ago': 'há 12 semanas',
  'me.activity.how':
    'Um quadrado por dia: quanto mais escuro, mais quebra-cabeças você concluiu naquele dia.',
  'me.activity.one': '1 dia com pelo menos um quebra-cabeça concluído',
  'me.activity.other': '{{n}} dias com pelo menos um quebra-cabeça concluído',
  'me.fav.none': 'A descobrir',
  'me.fav.noneHint': 'Conclua um quebra-cabeça para revelá-lo',
  'me.activity.none':
    'Nenhum quebra-cabeça concluído ainda: cada um escurecerá o quadrado do seu dia.',
  'together.cells.eyebrow': 'Células pintadas',
  'together.cells.what': 'por todos, desde segunda',
  'together.best.eyebrow': 'Melhor dia',
  'together.best.one': '{{n}} quebra-cabeça concluído',
  'together.best.other': '{{n}} quebra-cabeças concluídos',
  'together.top.eyebrow': 'O mais bem avaliado da semana',
  'together.top.votes.one': '{{n}} avaliação',
  'together.top.votes.other': '{{n}} avaliações',
  'together.top.done': 'Você já pintou',
  'together.top.locked': 'Em um pacote a desbloquear',
  'together.top.unlock': 'Desbloquear o pacote',
  'me.stat.finished': 'quebra-cabeças concluídos',
  'me.stat.daily': 'quebra-cabeças do dia',
  'me.stat.styles': 'estilos jogados',
  'me.tally': 'níveis desbloqueados',
  'me.section.milestones': 'Marcos',
  'me.section.families': 'Alfabetos',
  'me.section.categories': 'Categorias',
  'me.empty': 'Conclua um quebra-cabeça para desbloquear suas primeiras medalhas.',
  'badge.library': 'Biblioteca',
  'badge.daily': 'Quebra-cabeça do dia',
  'badge.share': 'Compartilhar',
  'badge.family.dots': 'Pontilhismo',
  'badge.family.shapes': 'Formas',
  'badge.family.hatching': 'Hachura',
  'badge.family.dice': 'Dados',
  'badge.family.ascii': 'ASCII',
  'badge.family.bricks': 'Tijolos',
  'badge.family.paint': 'Pintura',
  'badge.family.embroidery': 'Ponto cruz',
  'badge.goal.library':
    'Conclua quebra-cabeças da biblioteca. Os quebra-cabeças do dia têm sua própria medalha.',
  'badge.goal.daily':
    'Conclua quebra-cabeças do dia. Não precisa jogar dias seguidos: um dia recuperado pelo calendário também conta.',
  'badge.goal.share':
    'Compartilhe seus desenhos concluídos. Cada folha conta uma única vez, mesmo compartilhada várias vezes.',
  'badge.goal.family': 'Conclua quebra-cabeças em {{alphabets}}.',
  'badge.goal.category': 'Conclua quebra-cabeças da categoria {{category}}.',
  'badge.unit.puzzles.one': '{{n}} quebra-cabeça',
  'badge.unit.puzzles.other': '{{n}} quebra-cabeças',
  'badge.unit.shares.one': '{{n}} folha',
  'badge.unit.shares.other': '{{n}} folhas',
  'badge.next.bronze': 'para o Bronze',
  'badge.next.argent': 'para a Prata',
  'badge.next.or': 'para o Ouro',
  'badge.next.platine': 'para a Platina',
  'badge.next.prisme': 'para o Prisma',
  'badge.reached': 'Nível {{tier}} alcançado',
  'badge.earned': 'Conquistado',
  'badge.locked': 'A desbloquear',

  'celebrate.eyebrow': 'Novo nível',
  'celebrate.next': 'Próximo',
  'celebrate.later': 'Depois',
  'celebrate.see': 'Ver minhas medalhas',
  'celebrate.skipped.one': '{{tiers}} conquistado no caminho',
  'celebrate.skipped.other': '{{tiers}} conquistados no caminho',
  'celebrate.more.eyebrow': 'E também',
  'celebrate.more.title.one': 'Mais {{n}} nível',
  'celebrate.more.title.other': 'Mais {{n}} níveis',
  'celebrate.more.line': 'Você encontra todas nas suas medalhas.',
  'celebrate.welcome.eyebrow': 'Novidade: medalhas',
  'celebrate.welcome.title': 'Suas medalhas chegaram',
  'celebrate.welcome.count': 'níveis já conquistados',
  'celebrate.welcome.line': 'Elas contam tudo o que você já coloriu.',

  // ---------- prefs.ts / la page Réglages (index.html #prefsPage) ----------
  'prefs.lang.label': 'Idioma',
  'prefs.lang.hint': 'Muda todos os textos do aplicativo',
  'prefs.skin.label': 'Tema',
  'prefs.skin.hint': 'Como a tela se veste — a folha não muda',
  'prefs.sound.label': 'Som',
  'prefs.sound.hint': 'Uma nota por célula preenchida',
  'prefs.haptics.label': 'Vibração',
  'prefs.haptics.hint': 'Um toque na mão a cada célula',
  'prefs.motion.label': 'Animações',
  'prefs.motion.hint': 'Células que desabrocham, partículas, tremores',
  'prefs.compass.label': 'Bússola',
  'prefs.compass.hint': 'Uma seta até o símbolo quando ele sai da tela',
  'prefs.push.label': 'Notificações',
  'prefs.push.hint': 'Novos quebra-cabeças, lembretes',
  'prefs.inks.label': 'Cor das células',
  'prefs.inks.hint': 'Do que a folha se preenche',
  'prefs.marks.label': 'Marcador de células',
  'prefs.marks.hint': 'Como as células que faltam são marcadas',
  'prefs.markHues.label': 'Cor do marcador',
  'prefs.markHues.hint': 'Só afeta o marcador, não a tinta',
  'prefs.username.label': 'Apelido',
  'prefs.username.hint': 'Visível se um dia você quiser mostrar',
  'prefs.username.placeholder': 'Escolha um apelido',
  'prefs.username.random': 'Sugerir um apelido',
  'prefs.username.save': 'Salvar',
  'prefs.username.saving': 'Salvando…',
  'prefs.username.taken': 'Esse apelido já está em uso.',
  'prefs.username.rejected': 'Este apelido não pode ser usado. Escolha outro.',
  'prefs.username.failed': 'Não deu certo — tente de novo com conexão.',
  // ---------- o código de recuperação (prefs.ts) ----------
  'prefs.recovery.label': 'Recuperar meu progresso',
  'prefs.recovery.hint': 'Um código para guardar e continuar em outro celular',
  'prefs.recovery.mine':
    'Anote este código em algum lugar. Ele traz seu progresso de volta em outro aparelho, ou depois de reinstalar.',
  'prefs.recovery.loading': 'Um momento…',
  'prefs.recovery.offline': 'Código indisponível — tente de novo com conexão.',
  'prefs.recovery.copy': 'Copiar',
  'prefs.recovery.copied': 'Copiado',
  'prefs.recovery.theirs':
    'Já tem um código? Digite aqui: este aparelho vai assumir esse progresso, e o que você pintou aqui é mantido.',
  'prefs.recovery.placeholder': 'as palavras separadas por hifens',
  'prefs.recovery.use': 'Restaurar',
  'prefs.recovery.restoring': 'Restaurando…',
  'prefs.recovery.unknown': 'Este código não corresponde a nenhum progresso.',
  'prefs.recovery.failed': 'Não deu certo — tente de novo com conexão.',
  'prefs.recovery.done': 'Progresso recuperado.',
  'maker.eyebrow': 'Uma palavra do criador',
  'maker.title': 'Oi, eu sou o tidou44',
  'maker.who':
    'Sou desenvolvedor web independente e criei o Codigrid sozinho, pelo prazer de ver um desenho surgir célula por célula.',
  'maker.ask':
    'A publicidade é discreta, por escolha. Se você gosta do jogo, compartilhar ou avaliar me ajuda muito: tenho muitas ideias para o que vem por aí.',
  'maker.askWeb':
    'A publicidade é discreta, por escolha. Se você gosta do jogo, compartilhar me ajuda muito: tenho muitas ideias para o que vem por aí.',
  'maker.share': 'Compartilhar o jogo',
  'maker.rate': 'Avaliar',
  'maker.contact': 'Fale comigo',
  'maker.shareText':
    'Recomendo o Codigrid, um jogo de colorir lógico em que a grade não diz o que esconde. Por enquanto, disponível só para Android.',
  'prefs.ads.label': 'Preferências de anúncios',
  'prefs.ads.hint': 'Rever o seu consentimento para anúncios',
  'prefs.privacy.label': 'Privacidade',
  'prefs.privacy.hint': 'O que fica no aparelho e o que é enviado',
  'prefs.wipe.label': 'Apagar meus dados',
  'prefs.wipe.hint': 'A conta e todos os salvamentos, aqui e no servidor',
  'prefs.wipe.confirm': 'Apagar a conta e todos os salvamentos? Não dá para desfazer.',
  'prefs.wipe.progress': 'Apagando…',
  'prefs.wipe.failed': 'Não deu certo — nada foi apagado. Tente de novo com conexão.',

  // ---------- mark.ts (marcador de células, só no jogo — não está em shared/) ----------
  'mark.battant.label': 'Borda grossa',
  'mark.battant.hint': 'Uma borda grossa, o desenho intacto',
  'mark.hachures.label': 'Hachuras',
  'mark.hachures.hint': 'Listras sobre as células',
  'mark.cases.label': 'Cruz por célula',
  'mark.cases.hint': 'Uma cruz no centro de cada célula',
  'skin.clair': 'Claro',
  'skin.ciel': 'Céu',
  'skin.soir': 'Noite',

  'mark.hue.citron': 'Limão',
  'mark.hue.ambre': 'Âmbar',
  'mark.hue.rose': 'Rosa',
  'mark.tooltip': '{{label}} — {{hint}}',
  'mark.ariaLabel': '{{label}}. {{hint}}',

  // ---------- coach.ts et gestureDialog.ts : ce que l'écran apprend au début ----------
  'coach.boussole': 'O símbolo continua por ali. Toque na seta.',
  'coach.close': 'Ocultar esta dica',
  'ordre.later':
    'As cores entram <b>em ordem</b>: das claras às escuras e, no fim, os brancos para os brilhos. Esta vem depois.',
  'coach.hello.title': 'Oi, que bom te ver!',
  'coach.hello.body':
    'Eu sou a raposa do Codigrid. Cada grade esconde um desenho: preencha as células e veja ele aparecer.',
  'coach.hello.next': 'Me mostra',
  'coach.dlg.title': 'Duas formas de colorir',
  'coach.dlg.tap': '<b>Toque</b> em uma célula destacada: a zona inteira se preenche.',
  'coach.dlg.swipe': '<b>Deslize</b> sem levantar o dedo: cada zona que você cruzar se preenche.',
  'coach.dlg.next': 'Próximo',
  'coach.move.title': 'Para passear pelo desenho',
  'coach.move.drag':
    '<b>Arraste</b> a grade para se mover, começando por uma célula que não esteja destacada.',
  'coach.move.pinch': '<b>Com dois dedos</b>: afaste para ampliar, junte para reduzir.',
  'coach.dlg.go': 'Vamos lá',
  'piece.done.title': 'Muito bem!',
  'piece.done.body':
    'Era só o olhar: o resto do desenho está esperando por você. E ele não é o único.',
  'piece.done.packs': '<b>Centenas de desenhos</b> para baixar, organizados em pacotes',
  'piece.done.daily': 'Um <b>desenho do dia</b>, na aba Diário',
  'piece.done.create': 'Seus <b>próprios quebra-cabeças</b>, a partir das suas fotos',
  'piece.done.ask': 'Vamos terminar este desenho juntos, ou você quer ver a biblioteca?',
  'piece.done.finish': 'Terminar este desenho',
  'piece.done.library': 'Ver a biblioteca',
  'intro.bigDone':
    'Seu primeiro desenho está pronto! Centenas de outros estão esperando por você na biblioteca.',
  'piece.bravo': 'Muito bem!',
  'piece.line': 'Era só o olhar. O resto está esperando por você.',
  'prefs.intro.label': 'Rever a introdução',
  'prefs.intro.hint': 'O primeiro desenho, com a raposa',
  'prefs.coach.label': 'Rever os gestos',
  'prefs.coach.hint': 'Colorir, dar zoom, se mover, em animação',

  // ---------- dialogue de mise à jour (pwa.ts / index.html #update) ----------
  'update.title': 'Atualização disponível',
  'update.body':
    'Uma nova versão do Codigrid está pronta. Ela é instalada ao recarregar a página — seu progresso é mantido.',
  'update.reload': 'Recarregar',
  'update.reloading': 'Atualizando…',

  // ---------- écran de jeu (ui.ts / index.html .app) ----------
  'game.close': 'Fechar a folha',
  'game.finished': 'Concluído',
  'game.finishedSub': '{{todo}} células · {{w}}×{{h}} · {{alphabet}}',
  // La ligne « Avec toi, … » de la carte de fin (ui.ts, showPeers).
  'game.peers.sheet': 'Com você, {{n}} jogadores concluíram este desenho.',
  'game.peers.daily': 'Com você, {{n}} jogadores pintaram o quebra-cabeça do dia.',
  'game.peers.first': 'Você está entre os primeiros a concluir este desenho!',
  'game.peers.none': 'Ninguém tinha concluído antes de você!',
  'game.rate': 'Avaliar este quebra-cabeça',
  'game.replay': 'Rever a animação',
  'game.share': 'Compartilhar',
  'game.linkCopied': '✓ Link copiado',
  'shareSheet.image': 'Imagem',
  'shareSheet.imageSub': 'O desenho terminado',
  'shareSheet.video': 'Vídeo',
  'shareSheet.videoSub': 'O preenchimento, em 10 segundos',
  'shareSheet.imageBusy': 'Preparando a imagem…',
  'shareSheet.videoBusy': 'Filmando o seu preenchimento…',
  'shareSheet.imageReady': 'Sua imagem está pronta!',
  'shareSheet.videoReady': 'Seu vídeo está pronto!',
  'shareSheet.imageSend': 'Enviar a imagem',
  'shareSheet.videoSend': 'Enviar o vídeo',
  'shareSheet.imageSaved': '✓ Imagem salva',
  'shareSheet.videoSaved': '✓ Vídeo salvo',
  'shareSheet.other': 'Escolher outra coisa',
  'shareSheet.failed': 'Não foi possível preparar o arquivo. Tente de novo daqui a pouco.',
  'shareSheet.msg.mineVideo':
    'Minha foto vira um desenho, célula por célula. Feito no Codigrid, grátis no Android:\n{{url}}',
  'shareSheet.msg.mineImage':
    'Minha foto, transformada em desenho para pintar no Codigrid. Grátis no Android:\n{{url}}',
  'shareSheet.msg.video':
    'Veja este desenho aparecer célula por célula. Codigrid, grátis no Android:\n{{url}}',
  'shareSheet.msg.image': 'Pintado célula por célula no Codigrid. Grátis no Android:\n{{url}}',
  'game.shareText':
    'Acabei de terminar uma folha no Codigrid — um jogo de colorir lógico em que a grade não diz o que esconde.',

  // ---------- splash.ts ----------
  // Longueur tenue près de l'original — cf. l'avertissement à côté de L1/L2 dans splash.ts :
  // fit() dimensionne le texte du splash sur le nombre exact de caractères de ces deux lignes.
  'splash.line1': 'Uma célula por vez,',
  'splash.line2': 'a imagem se revela',

  // ---------- report.ts (a falha ao iniciar) ----------
  'crash.boot': 'O jogo não conseguiu iniciar.',

  // ---------- share.ts (texte injecté dans p.html au chargement) ----------
  'share.title': '“{{title}}”, concluído',
  'share.meta': '{{w}} × {{h}} · {{alphabet}}',
  'share.docTitle': '{{title}} — Codigrid',

  // ---------- p.html (le squelette statique de la page de partage) ----------
  'share.page.metaDescription':
    'Codigrid, um jogo de colorir lógico: uma grade de símbolos codificados e o desenho que aparece sob os dedos.',
  'share.page.docTitleDefault': 'Uma folha concluída — Codigrid',
  'share.page.title': 'Uma folha concluída',
  'share.page.pitch':
    'Codigrid é um jogo de colorir lógico. Cada célula carrega um símbolo codificado, você preenche um símbolo de cada vez, e o desenho aparece sob a sua mão. <em>A grade não diz o que esconde.</em>',
  'share.page.play': 'Jogar agora',
  'share.page.store': 'Disponível no Google Play',
  'share.page.fine': 'Grátis, sem conta. Uma folha nova todo dia.',
  'share.page.fail': 'Este desenho não está mais disponível.',
  'share.page.again': '↻ Rever o preenchimento',
};

export default pt;
